/**
 * Gmail Connector - OAuth2 Flow
 * Handles Gmail OAuth authentication and token exchange
 */

import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Token from localStorage
const token = localStorage.getItem('token');
 
// Prevent duplicate token exchange (React Strict Mode double mount / callback re-entry)
let isProcessingGmailTokenExchange = false;

/**
 * Get Gmail credentials and create OAuth token
 * Initiates OAuth flow by redirecting to Google's consent screen
 */
export const getGmailCredentialsAndCreateToken = async (ctdId, mcId, masterConnectors) => {
  try {
    console.log('🔑 Initiating Gmail OAuth flow...');
    console.log('Connector ID (ctdId):', ctdId);
    console.log('Master Connector ID (mcId):', mcId);

    // 1) Find Gmail master connector
    const gmailMasterConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === mcId?.toString() ||
       mc.mcId?.toString() === mcId?.toString()) &&
      mc.mcName === "Gmail"
    );

    if (!gmailMasterConnector) {
      throw new Error('Gmail master connector not found');
    }

    console.log('Gmail master connector found:', gmailMasterConnector);

    // 2) Fetch encrypted client_id and client_secret from backend
    const paramsRes = await fetch(buildConnectorParametersDecryptUrl(ctdId), {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!paramsRes.ok) {
      throw new Error('Failed to fetch connector parameters');
    }

    const connectorParams = await paramsRes.json();

    const clientId = connectorParams.find(p => p.scpName === 'clientid')?.scpValue;

    if (!clientId) {
      throw new Error('Client ID not found in connector parameters');
    }

    console.log('Client ID retrieved:', clientId.substring(0, 30) + '...');

    // 4) Store connector ID for post-redirect completion
    localStorage.setItem('PendingGmailConnectorId', ctdId);
    localStorage.setItem('PendingGmailMcId', mcId);

    // 5) Build Google OAuth URL (frontend-side, like GoogleDrive)
    const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;
    const scopes = [
      'https://www.googleapis.com/auth/gmail.send',
      'https://www.googleapis.com/auth/gmail.readonly',
    ].join(' ');

    // Use Master Connector auth URL
    const authEndpoint = gmailMasterConnector.mcAuthurl;
    if (!authEndpoint) {
      throw new Error('Gmail Authentication URL is missing from Master Connector data');
    }

    const googleAuthUrl =
      `${authEndpoint}${authEndpoint.includes('?') ? '&' : '?'}` +
      `client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(scopes)}` +
      `&access_type=offline` +  // Request refresh token
      `&prompt=consent` +        // Force consent to ensure refresh token
      `&state=${encodeURIComponent(ctdId)}`;

    console.log('Redirecting to Google OAuth...');

    // 6) Redirect to Google OAuth (frontend-side redirect, no backend call)
    window.location.href = googleAuthUrl;

    // Return null to indicate we're redirecting (prevents status update)
    return null;

  } catch (error) {
    console.error('Error initiating Gmail OAuth:', error);
    throw error;
  }
};

/**
 * Exchange Gmail OAuth authorization code for access token
 * Called after user grants permission and returns from Google
 */
export const exchangeGmailAuthCodeForToken = async (authCode, ctdId, mcId) => {
  try {
    console.log('Master Connector ID:', mcId);
 
    if (isProcessingGmailTokenExchange) {
      console.warn('Gmail token exchange already in progress; skipping duplicate to avoid auth code reuse / invalid_grant');
      return null;
    }
 
    isProcessingGmailTokenExchange = true;

    // Get JWT token from localStorage
    const jwtToken = localStorage.getItem('token');

    // Call backend to exchange auth code for tokens. redirect_uri must match exactly
    // the one used when building the auth URL, or Google returns invalid_grant.
    const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;
    const params = { code: authCode, mcId: mcId, ctdId: ctdId };
    if (redirectUri) params.redirectUri = redirectUri;

    const response = await fetch(`${BACKEND_URL}synctrack/gmail/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'Authorization': `Bearer ${jwtToken}`
      },
      body: new URLSearchParams(params)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Backend error response:', errorText);
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
    }

    const tokenData = await response.json();

    console.log('✅ Gmail token received successfully');
    console.log('Access Token:', tokenData.access_token ? `${tokenData.access_token.substring(0, 30)}...` : 'Not provided');
    console.log('Refresh Token:', tokenData.refresh_token ? `${tokenData.refresh_token.substring(0, 20)}...` : 'Not provided');
    console.log('Expires In:', tokenData.expires_in, 'seconds');

    return {
      access_token: tokenData.access_token,
      refresh_token: tokenData.refresh_token,
      expires_in: tokenData.expires_in || 3600,
      scope: tokenData.scope,
      token_type: tokenData.token_type || 'Bearer'
    };

  } catch (error) {
    throw error;
  } finally {
    isProcessingGmailTokenExchange = false;
  }
};

/**
 * Refresh Gmail access token using refresh token
 */
export const refreshGmailToken = async (refreshToken, mcId, ctdId) => {
  try {
    console.log('🔄 Refreshing Gmail token...');

    const jwtToken = localStorage.getItem('token');

    const response = await fetch(`${BACKEND_URL}synctrack/gmail/refreshToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'Authorization': `Bearer ${jwtToken}`
      },
      body: new URLSearchParams({
        refreshToken: refreshToken,
        mcId: mcId,
        ctdId: ctdId
      })
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const tokenData = await response.json();

    console.log('✅ Gmail token refreshed successfully');

    return {
      access_token: tokenData.access_token,
      refresh_token: tokenData.refresh_token || refreshToken, // Keep old refresh token if not returned
      expires_in: tokenData.expires_in || 3600
    };

  } catch (error) {
    console.error('Error refreshing Gmail token:', error);
    throw error;
  }
};
